/**
 * Authenticated fetch wrapper for the Atlas HTTP API.
 *
 * Uses Amplify's fetchAuthSession to get the Cognito ID token and attaches it
 * as a Bearer token on every request. Used for CRUD operations on tasks,
 * calendar events, and reminders via the API Gateway.
 */

import { fetchAuthSession } from "aws-amplify/auth";

/**
 * Makes an authenticated HTTP request to the Atlas API.
 *
 * @param baseUrl - API Gateway base URL
 * @param path - API path (e.g., "/tasks", "/events")
 * @param options - Standard RequestInit options (method, body, etc.)
 * @returns Parsed JSON response
 * @throws Error if not authenticated or if request fails
 */
export async function apiClient<T>(
  baseUrl: string,
  path: string,
  options?: RequestInit,
): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  if (!token) {
    throw new Error("Not authenticated");
  }

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...options?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }

  return response.json() as Promise<T>;
}
